import { createContext, useCallback, useMemo, useState, type ReactNode } from "react";
import { FullScreenSuccessModal } from "./FullScreenSuccessModal";

export interface SuccessContent {
  title: string;
  subtitle: string;
  /** Muted footer hint, e.g. "Antippen zum Fortfahren". */
  hint: string;
}

export interface SuccessModalContextValue {
  /** Show the full-screen success overlay. Fire-and-forget; the overlay dismisses itself on tap. */
  showSuccess: (content: SuccessContent) => void;
}

export const SuccessModalContext = createContext<SuccessModalContextValue | null>(null);

/**
 * Mounts a single FullScreenSuccessModal and exposes `showSuccess` via context. Any descendant can
 * trigger the overlay with one line (see useSuccessModal); open/close is owned entirely here.
 */
export function SuccessModalProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SuccessContent | null>(null);
  const [open, setOpen] = useState(false);

  // Keep the last content mounted while the close animation plays; it's replaced on the next show.
  const showSuccess = useCallback((next: SuccessContent) => {
    setContent(next);
    setOpen(true);
  }, []);

  const value = useMemo<SuccessModalContextValue>(() => ({ showSuccess }), [showSuccess]);

  return (
    <SuccessModalContext.Provider value={value}>
      {children}
      <FullScreenSuccessModal
        open={open}
        title={content?.title ?? ""}
        subtitle={content?.subtitle ?? ""}
        hint={content?.hint ?? ""}
        onDismiss={() => setOpen(false)}
      />
    </SuccessModalContext.Provider>
  );
}
